import { useEffect, useRef, useState } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useAppDispatch } from '../../store/hook';
import { openAlert } from '../../store/features/modalSlice';

type SectionForm<TForm extends FieldValues> = Pick<
  UseFormReturn<TForm>,
  'getValues' | 'setValue' | 'trigger' | 'watch' | 'reset'
>;

/**
 * 폼 하나를 영역별로 나눠서 수정→검증→저장하는 화면(LMS 학생 상세)의 상태 머신.
 * 영역마다 다루는 필드를 `fields`로 받아, 검증·되돌리기·공백 제거를 그 범위에서만 수행한다.
 * 변경 감지·저장 API 호출은 `save`로 위임받는다.
 *
 * 학생 내 정보는 카드마다 폼을 따로 가지므로 이 훅을 쓰지 않는다.
 * (`hooks/student/profile/useSectionSave` 로 저장 처리만 공유한다)
 */
export function useSectionEditForm<
  TForm extends FieldValues,
  K extends string,
>({
  fields,
  form,
  getOrigin,
  save,
  onSaved,
}: {
  fields: Record<K, (keyof TForm & string)[]>;
  form: SectionForm<TForm>;
  /** 취소 시 되돌릴 원본 값. */
  getOrigin: () => Partial<TForm> | undefined;
  /** 변경분 계산 + API 호출까지 화면이 담당한다. 변경이 없었으면 false. */
  save: (section: K) => Promise<boolean>;
  /** 저장 성공 후 재조회. */
  onSaved: () => void;
}) {
  const dispatch = useAppDispatch();
  const { getValues, setValue, trigger, watch, reset } = form;

  /** 현재 수정 중인 영역. null이면 전체 조회 상태 */
  const [editSection, setEditSection] = useState<K | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  /** 저장 중복 요청 방지. 화면 갱신을 기다리지 않고 즉시 막아야 하므로 state와 별도로 둔다. */
  const savingRef = useRef(false);
  /** 저장하기에서 검증에 걸린 영역. 이 영역은 값을 고치는 즉시 재검증한다. */
  const [revalidateSection, setRevalidateSection] = useState<K | null>(null);

  /**
   * 저장하기에서 검증에 걸린 뒤에는 값을 고치는 즉시 오류 표시가 사라지도록 한다.
   * trigger로 직접 검증하면 formState.isSubmitted가 켜지지 않아
   * react-hook-form의 reValidateMode(기본 onChange)가 동작하지 않기 때문에 직접 구독한다.
   */
  useEffect(() => {
    if (!revalidateSection) return;

    const isSectionField = (name: string) =>
      fields[revalidateSection].some(
        // 보호자처럼 배열인 필드는 하위 경로(parents.0.name)까지 포함한다.
        (field) => name === field || name.startsWith(`${field}.`),
      );

    const subscription = watch((_, { name }) => {
      // 보호자 추가/삭제처럼 특정 필드의 변경이 아닌 경우는 건너뛴다.
      if (!name || !isSectionField(name)) return;
      trigger(name as Path<TForm>);
    });

    return () => subscription.unsubscribe();
  }, [revalidateSection, watch, trigger, fields]);

  /**
   * 저장 전 해당 영역 입력값의 앞뒤 공백을 제거한다.
   * 화면 값 자체를 바꾸므로 이후의 검증/변경 비교도 공백이 제거된 값으로 수행된다.
   */
  const trimSectionValues = (section: K) => {
    const trim = (path: Path<TForm>) => {
      const value = getValues(path);
      if (typeof value !== 'string' || value === value.trim()) return;
      setValue(path, value.trim() as never);
    };

    fields[section].forEach((field) => {
      const value = getValues(field as Path<TForm>);
      // 보호자 목록처럼 배열인 필드는 항목마다 하위 입력을 훑는다.
      if (Array.isArray(value)) {
        value.forEach((item: object, index: number) =>
          Object.keys(item ?? {}).forEach((key) =>
            trim(`${field}.${index}.${key}` as Path<TForm>),
          ),
        );
        return;
      }
      trim(field as Path<TForm>);
    });
  };

  const startEdit = (section: K) => {
    setEditSection(section);
    setRevalidateSection(null);
  };

  /** 수정 중인 영역의 값만 조회 시점으로 되돌린다. (다른 영역 입력은 건드리지 않는다) */
  const cancel = (section: K) => {
    const origin = getOrigin();
    const restored = fields[section].reduce((acc, field) => {
      const value = origin?.[field];
      return {
        ...acc,
        // useFieldArray는 배열이 아닌 값을 무시하므로 배열 필드는 빈 배열로 맞춘다.
        [field]:
          value === undefined && Array.isArray(getValues(field as Path<TForm>))
            ? []
            : value,
      };
    }, {});
    reset({ ...getValues(), ...restored } as TForm);
    setEditSection(null);
    setRevalidateSection(null);
  };

  /** 수정 중인 영역만 저장한다. 검증도 그 영역의 필드만 수행한다. */
  const submit = async (section: K) => {
    // 보호자는 배열이 통째로 교체되므로 중복 요청이 특히 위험하다.
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);

    try {
      trimSectionValues(section);

      // 유효성 검사는 각 입력의 rules에서 처리한다. (보호자 항목은 parentRules)
      // shouldFocus: 오류 항목이 스크롤 밖에 있어도 사용자가 알 수 있도록 첫 오류로 이동한다.
      const isValid = await trigger(fields[section] as Path<TForm>[], {
        shouldFocus: true,
      });
      // 한 번 검증에 걸린 뒤부터는 입력을 고치는 즉시 오류 표시가 갱신된다.
      setRevalidateSection(isValid ? null : section);
      if (!isValid) return;

      try {
        const changed = await save(section);
        if (!changed) {
          dispatch(openAlert({ message: '변경된 내용이 없습니다.' }));
          setEditSection(null);
          return;
        }

        onSaved();
        dispatch(openAlert({ message: '수정이 완료되었습니다.' }));
        setEditSection(null);
      } catch (err) {
        console.error(err);
        // 네트워크/500 에러는 인터셉터가 이미 알럿을 띄운다. (__handled)
        if ((err as { __handled?: boolean })?.__handled) return;
        const message = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        dispatch(openAlert({ message: message ?? '수정에 실패했습니다.' }));
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return { editSection, isSaving, startEdit, cancel, submit };
}
